/**
 * Optimized Query Hooks
 * Prevents multiple fetches and provides better caching strategies
 */

import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { apiClient } from "@finitless/shared/lib";
import { isEndpointEnabled } from "@finitless/shared/config";
import { getCurrentDateRange } from "../lib/utils";
import { buildQueryParams } from "../lib/globalUtils";

/**
 * Hook to get user profile with optimized caching
 * This prevents multiple components from triggering the same query
 * For AccountPage, we use store data instead of API calls
 */
export const useOptimizedUserProfile = () => {
  // Return a mock query that uses store data instead of API
  return {
    data: null, // Will be handled by AccountPage using store data
    isLoading: false,
    error: null,
    isSuccess: true,
    isError: false,
  };
};

/**
 * Hook to get orders with optimized caching
 * Prevents multiple refetch and reduces API calls
 * IMPORTANT: ALWAYS fetches only AI-generated orders (orders with conversation_id)
 * The conversation_id filter is applied by buildWhereClause in orderConstants.js
 */
export const useOptimizedOrders = (params = {}) => {
  // Build query parameters
  let queryParams = { ...params };

  // If where is provided as an object, stringify it
  if (params.where) {
    queryParams = {
      ...queryParams,
      where:
        typeof params.where === "string"
          ? params.where
          : JSON.stringify(params.where),
    };
  }

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["orders", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/orders${queryString ? `?${queryString}` : ""}`,
      );
      return {
        orders: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: isEndpointEnabled("dashboard", "orders"), // Controlled by config
    staleTime: 10 * 60 * 1000, // 10 minutos
    refetchInterval: false, // DISABLED
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("dashboard", "orders"), // Controlled by config
    refetchOnReconnect: false,
    retry: false, // DISABLED
  });
};

/**
 * Hook to get customers with optimized caching
 * Prevents multiple refetch and reduces API calls
 */
export const useOptimizedCustomers = (filters = {}) => {
  return useQuery({
    queryKey: ["customers", filters],
    queryFn: async () => {
      const response = await apiClient.get("/customers", {
        params: filters,
      });
      return response;
    },
    enabled: isEndpointEnabled("dashboard", "customers"), // Controlled by config
    staleTime: 10 * 60 * 1000, // 10 minutos
    refetchInterval: false, // DISABLED
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("dashboard", "customers"), // Controlled by config
    refetchOnReconnect: false,
    retry: false, // DISABLED
  });
};

/**
 * Hook to prefetch data for better UX
 * This can be used to prefetch data before it's needed
 */
export const usePrefetchData = () => {
  const queryClient = useQueryClient();

  const prefetchUserProfile = () => {
    queryClient.prefetchQuery({
      queryKey: ["user", "profile"],
      queryFn: async () => {
        const response = await fetch("/api/auth/me");
        return response.json();
      },
      staleTime: 10 * 60 * 1000, // 10 minutes
    });
  };

  const prefetchDashboardMetrics = () => {
    queryClient.prefetchQuery({
      queryKey: ["dashboard", "metrics"],
      queryFn: async () => {
        const response = await fetch("/api/dashboard/metrics");
        return response.json();
      },
      staleTime: 2 * 60 * 1000, // 2 minutes
    });
  };

  return {
    prefetchUserProfile,
    prefetchDashboardMetrics,
  };
};

/**
 * Hook to get today's orders count from reports endpoint
 * Fetches orders data and calculates total count
 */
export const useTodaysOrders = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["reports", "orders", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/reports/orders${queryString ? `?${queryString}` : ""}`,
      );

      // Calculate total orders from the result
      let totalOrders = 0;
      if (response?.result?.length > 0) {
        for (const order of response.result) {
          totalOrders += parseInt(order.orders) || 0;
        }
      }

      return {
        totalOrders,
        rawData: response?.result || [],
      };
    },
    enabled: isEndpointEnabled("reports", "orders"),
    staleTime: 2 * 60 * 1000, // 2 minutes - orders change frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("reports", "orders"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get sales data from reports endpoint with configurable lapse
 * Fetches sales data and calculates total amount for any time period
 */
export const useSales = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["reports", "sales", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/reports/sales${queryString ? `?${queryString}` : ""}`,
      );

      // Calculate total sales from the result
      let totalSales = 0;
      if (response?.result?.length > 0) {
        for (const sale of response.result) {
          totalSales += parseFloat(sale.sales) || 0;
        }
      }

      return {
        totalSales,
        rawData: response?.result || [],
      };
    },
    enabled: isEndpointEnabled("reports", "sales"),
    staleTime: 2 * 60 * 1000, // 2 minutes - sales change frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("reports", "sales"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get today's sales total from reports endpoint
 * Uses the parameterized useSales hook with 'today' lapse
 */
export const useTodaysSales = (params = {}) => {
  return useSales(params);
};

/**
 * Hook to get month's sales total from reports endpoint
 * Uses the parameterized useSales hook with calculated current month date range
 */
export const useMonthsSales = () => {
  const lapse = getCurrentDateRange();
  return useSales({ lapse });
};

/**
 * Hook to get time spent to accept orders with dynamic parameters
 * Fetches data from orders_accept_spend endpoint with configurable filters
 * @param {Object} params - Query parameters
 * @param {string} params.lapse - Time lapse (default: 'today')
 * @param {string} params.timezone - Timezone (default: current timezone)
 * @param {Array} params.businessIds - Array of business IDs
 * @param {string} params.app_id - App ID
 * @param {Array} params.franchises_id - Array of franchise IDs
 */
export const useOrdersAcceptSpend = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["reports", "orders_accept_spend", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/reports/orders_accept_spend${queryString ? `?${queryString}` : ""}`,
      );

      return {
        timeInSeconds: response?.result || 0,
        rawData: response,
        params: params,
      };
    },
    enabled: isEndpointEnabled("reports", "orders_accept_spend"),
    staleTime: 2 * 60 * 1000, // 2 minutes - time data changes frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("reports", "orders_accept_spend"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get time spent waiting for ready for pickup with dynamic parameters
 * Fetches data from arrived_pickup_spend endpoint with configurable filters
 * @param {Object} params - Query parameters
 * @param {string} params.lapse - Time lapse (default: 'today')
 * @param {string} params.timezone - Timezone (default: current timezone)
 * @param {Array} params.businessIds - Array of business IDs
 * @param {string} params.app_id - App ID
 * @param {Array} params.franchises_id - Array of franchise IDs
 */
export const useArrivedPickupSpend = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["reports", "arrived_pickup_spend", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/reports/arrived_pickup_spend${queryString ? `?${queryString}` : ""}`,
      );

      return {
        timeInSeconds: response?.result || 0,
        rawData: response,
        params: params,
      };
    },
    enabled: isEndpointEnabled("reports", "arrived_pickup_spend"),
    staleTime: 2 * 60 * 1000, // 2 minutes - time data changes frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("reports", "arrived_pickup_spend"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get customer satisfaction ratings with dynamic parameters
 * Fetches data from customer_satisfaction endpoint with configurable filters
 * @param {Object} params - Query parameters
 * @param {string} params.lapse - Time lapse (default: 'today')
 * @param {string} params.timezone - Timezone (default: current timezone)
 * @param {Object} params.where - Where clause object (from buildSatisfactionWhereClause)
 * @param {string} params.app_id - App ID
 * @param {Array} params.franchises_id - Array of franchise IDs
 */
export const useCustomerSatisfaction = (params = {}) => {
  let queryParams = { ...params };

  if (params.where) {
    queryParams = {
      ...queryParams,
      where:
        typeof params.where === "string"
          ? params.where
          : JSON.stringify(params.where),
    };
  }

  const queryString = buildQueryParams(queryParams);

  return useQuery({
    queryKey: ["reports", "customer_satisfaction", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/reports/customer_satisfaction${queryString ? `?${queryString}` : ""}`,
      );

      return {
        satisfaction: response?.result || {},
        rawData: response,
        params: params,
      };
    },
    enabled: isEndpointEnabled("reports", "customer_satisfaction"),
    staleTime: 5 * 60 * 1000, // 5 minutes - satisfaction data changes less frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("reports", "customer_satisfaction"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get today's registered users count from reports endpoint
 * Fetches users data and calculates total count for today
 * @param {Object} params - Query parameters
 * @param {string} params.lapse - Time lapse (default: 'today')
 * @param {string} params.timezone - Timezone (default: current timezone)
 * @param {Array} params.businessIds - Array of business IDs
 * @param {string} params.app_id - App ID
 * @param {Array} params.franchises_id - Array of franchise IDs
 */
export const useTodaysUsers = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["reports", "users", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/reports/users${queryString ? `?${queryString}` : ""}`,
      );

      // Calculate total users from the result array length
      const totalUsers = response?.result?.length || 0;

      return {
        totalUsers,
        rawData: response?.result || [],
        params: params,
      };
    },
    enabled: isEndpointEnabled("reports", "users"),
    staleTime: 2 * 60 * 1000, // 2 minutes - user data changes frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("reports", "users"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get top selling products from reports endpoint
 * Fetches top selling products data with configurable filters
 * @param {Object} params - Query parameters
 * @param {string} params.lapse - Time lapse (default: 'today')
 * @param {string} params.timezone - Timezone (default: current timezone)
 * @param {Array} params.businessIds - Array of business IDs
 * @param {string} params.app_id - App ID
 * @param {Array} params.franchises_id - Array of franchise IDs
 */
export const useTopSelling = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["reports", "top_selling", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/reports/top_selling${queryString ? `?${queryString}` : ""}`,
      );

      return {
        products: response?.result || [],
        rawData: response,
        params: params,
      };
    },
    enabled: isEndpointEnabled("reports", "top_selling"),
    staleTime: 5 * 60 * 1000, // 5 minutes - product data changes less frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("reports", "top_selling"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get top categories from reports endpoint
 * Fetches top categories data with configurable filters
 * @param {Object} params - Query parameters
 * @param {string} params.lapse - Time lapse (default: 'today')
 * @param {string} params.timezone - Timezone (default: current timezone)
 * @param {Array} params.businessIds - Array of business IDs
 * @param {string} params.app_id - App ID
 * @param {Array} params.franchises_id - Array of franchise IDs
 */
export const useTopCategories = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["reports", "top_categories", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/reports/top_categories${queryString ? `?${queryString}` : ""}`,
      );

      return {
        categories: response?.result || [],
        rawData: response,
        params: params,
      };
    },
    enabled: isEndpointEnabled("reports", "top_categories"),
    staleTime: 5 * 60 * 1000, // 5 minutes - category data changes less frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("reports", "top_categories"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get businesses with comprehensive state management
 * Handles loading, error, empty, and success states
 */
export const useBusinesses = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ["brand-businesses", params],
    queryFn: async () => {
      const response = await apiClient.get("/backoffice/businesses", {
        params: {
          params: "id,name,slug,address",
          where: JSON.stringify([
            {
              attribute: "enabled",
              value: true,
            },
          ]),
        },
      });
      return response;
    },
    enabled: isEndpointEnabled("business", "list") && options.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount:
      isEndpointEnabled("business", "list") && options.enabled !== false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options, // Spread additional options
  });
};

/**
 * Hook to get order messages with optimized caching
 * Fetches messages for a specific order with configurable parameters
 * @param {Object} params - Query parameters
 */
export const useOrderMessages = (orderId, params = {}) => {
  const queryString = buildQueryParams({
    mode: "dashboard",
    ...params,
  });

  return useQuery({
    queryKey: ["orderMessages", orderId, params],
    queryFn: async () => {
      if (!orderId) return [];

      const response = await apiClient.get(
        `/orders/${orderId}/messages${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result || [];
    },
    enabled: !!orderId && isEndpointEnabled("orders", "messages"),
    staleTime: 5 * 60 * 1000, // 5 minutes - messages don't change frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: !!orderId && isEndpointEnabled("orders", "messages"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get categories of a specific business
 * Fetches categories for a given business ID with minimal fields
 * @param {number|string} businessId - Business ID to fetch categories for
 */
export const useBusinessCategories = (businessId, options = {}) => {
  return useQuery({
    queryKey: ["business-categories", businessId],
    queryFn: async () => {
      if (!businessId) {
        return [];
      }

      const response = await apiClient.get(
        `/business/${businessId}/categories`,
        {
          params: {
            params: "id,name,enabled",
          },
        },
      );

      return response;
    },
    enabled:
      !!businessId &&
      isEndpointEnabled("business", "categories") &&
      options.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount:
      !!businessId &&
      isEndpointEnabled("business", "categories") &&
      options.enabled !== false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
};

/**
 * Hook to get order AI messages with optimized caching
 * Fetches messages for a specific AI conversation with pagination support
 * @param {number} conversationId - Conversation ID
 * @param {Object} params - Query parameters
 * @param {string} params.params - Comma-separated fields (e.g., 'id,message,role,created_at')
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 50)
 * @param {string} params.orderBy - Field to order by (e.g., 'created_at' or '-created_at')
 * @param {string} params.where - Filter conditions in JSON format
 */
export const useOrderAiMessages = (conversationId, params = {}) => {
  const queryString = buildQueryParams({
    params:
      "id,message,role,created_at,group_rank,is_public,channel_message_id",
    page: 1,
    page_size: 50,
    orderBy: "created_at",
    ...params,
  });

  return useQuery({
    queryKey: ["orderAiMessages", conversationId, params],
    queryFn: async () => {
      if (!conversationId) return { messages: [], pagination: {} };

      const response = await apiClient.get(
        `/ai/conversations/${conversationId}/messages${
          queryString ? `?${queryString}` : ""
        }`,
      );
      return {
        messages: response?.result || [],
        pagination: response?.pagination || {},
      };
    },
    enabled: !!conversationId,
    staleTime: 2 * 60 * 1000, // 2 minutes - AI messages might change more frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to send order messages with optimistic updates
 * Sends a message to a specific order and updates the cache
 * @param {number} orderId - Order ID to send message to
 * @param {Object} options - Additional options
 * @param {Function} options.onSuccess - Callback on successful send
 * @param {Function} options.onError - Callback on error
 */
export const useSendOrderMessage = (orderId, options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (messageData) => {
      if (!orderId) throw new Error("Order ID is required");

      const {
        message,
        canRead = {
          customer: true,
          administrator: true,
          business: true,
          driver: false,
        },
      } = messageData;

      // Build can_see array based on permissions
      const _canRead = [];
      if (canRead.customer) _canRead.push(3);
      if (canRead.administrator) _canRead.push(0);
      if (canRead.business) _canRead.push(2);
      if (canRead.driver) _canRead.push(4);

      const body = {
        comment: message,
        type: 2,
        can_see: _canRead.join(","),
      };

      const response = await apiClient.post(
        `/orders/${orderId}/messages`,
        body,
      );
      return response?.result || response;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch order messages
      queryClient.invalidateQueries(["orderMessages", orderId]);

      // Call success callback if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables);
      }
    },
    onError: (error, variables) => {
      // Call error callback if provided
      if (options.onError) {
        options.onError(error, variables);
      }
    },
  });
};

/**
 * Hook to get AI metrics overview
 * Fetches comprehensive AI metrics including orders, revenue, and conversations
 * @param {Object} params - Query parameters
 * @param {number} params.business_id - Optional business ID to filter metrics
 * @param {number} params.franchise_id - Optional franchise ID to filter metrics
 */
export const useAiMetricsOverview = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["ai", "metrics", "overview", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/metrics/overview${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result || {};
    },
    enabled: isEndpointEnabled("ai", "metrics"),
    staleTime: 2 * 60 * 1000, // 2 minutes - AI metrics change frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("ai", "metrics"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to get AI orders metrics
 * Fetches detailed order metrics for AI-assisted orders
 * @param {Object} params - Query parameters
 * @param {number} params.business_id - Optional business ID to filter metrics
 * @param {number} params.franchise_id - Optional franchise ID to filter metrics
 */
export const useAiOrdersMetrics = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["ai", "metrics", "orders", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/metrics/orders${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result || {};
    },
    enabled: isEndpointEnabled("ai", "ordersMetrics"),
    staleTime: 2 * 60 * 1000, // 2 minutes - AI orders metrics change frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("ai", "ordersMetrics"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useAiChatsMetrics = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["ai", "metrics", "chats", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/metrics/chats${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result || {};
    },
    enabled: isEndpointEnabled("ai", "chatsMetrics"),
    staleTime: 2 * 60 * 1000, // 2 minutes - AI chats metrics change frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("ai", "chatsMetrics"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useAiProductsMetrics = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["ai", "metrics", "products", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/metrics/products${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result || {};
    },
    enabled: isEndpointEnabled("ai", "productsMetrics"),
    staleTime: 5 * 60 * 1000, // 5 minutes - AI products metrics change less frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("ai", "productsMetrics"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useAiAgentsMetrics = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["ai", "metrics", "agents", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/metrics/agents${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result || {};
    },
    enabled: isEndpointEnabled("ai", "agentsMetrics"),
    staleTime: 2 * 60 * 1000, // 2 minutes - AI agents metrics change frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("ai", "agentsMetrics"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

export const useAiPerformanceMetrics = (params = {}) => {
  const queryString = buildQueryParams(params);

  return useQuery({
    queryKey: ["ai", "metrics", "performance", params],
    queryFn: async () => {
      const response = await apiClient.get(
        `/ai/metrics/performance${queryString ? `?${queryString}` : ""}`,
      );
      return response?.result || {};
    },
    enabled: isEndpointEnabled("ai", "performanceMetrics"),
    staleTime: 2 * 60 * 1000, // 2 minutes - AI performance metrics change frequently
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: isEndpointEnabled("ai", "performanceMetrics"),
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      // Retry up to 2 times for network errors, but not for 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to check if queries are currently fetching
 * Useful for showing global loading states
 */
export const useQueryStatus = () => {
  const queryClient = useQueryClient();

  const isFetching = queryClient.isFetching();
  const isMutating = queryClient.isMutating();

  return {
    isFetching,
    isMutating,
    isLoading: isFetching > 0 || isMutating > 0,
  };
};

export default {
  useOptimizedUserProfile,
  useOptimizedOrders,
  useOptimizedCustomers,
  useTodaysOrders,
  useSales,
  useTodaysSales,
  useMonthsSales,
  useOrdersAcceptSpend,
  useArrivedPickupSpend,
  useCustomerSatisfaction,
  useTodaysUsers,
  useTopSelling,
  useTopCategories,
  useBusinesses,
  useBusinessCategories,
  useOrderMessages,
  useOrderAiMessages,
  useSendOrderMessage,
  usePrefetchData,
  useQueryStatus,
  useAiMetricsOverview,
  useAiOrdersMetrics,
  useAiChatsMetrics,
  useAiProductsMetrics,
  useAiAgentsMetrics,
  useAiPerformanceMetrics,
};
